import { useState, useCallback } from "react";
import { ScanLine, ClipboardCheck, History } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { beep } from "@/lib/scanFx";
import { PageHeader } from "@/components/bits";
import { CameraScanner } from "@/components/CameraScanner";
import { PhotoCapture, OverTargetDialog, TargetChip } from "@/components/finalCheckBits";
import { FinalCheckHistory } from "@/components/FinalCheckHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export const newClientId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/** Material Control = FINAL CHECK material: scan → data marking → qty aktual → foto → konfirmasi IYA/TIDAK → simpan.
 *  Qty IYA menjadi saldo final check yang mengesahkan (APPROVED) progress pada alokasi yang sama. */
export default function MaterialControl() {
  const [code, setCode] = useState("");
  const [allocs, setAllocs] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [qty, setQty] = useState("");
  const [photos, setPhotos] = useState([]);
  const [confirmed, setConfirmed] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(null);
  const [overNotes, setOverNotes] = useState({});
  const [clientId, setClientId] = useState(newClientId());
  const [refreshKey, setRefreshKey] = useState(0);

  const reset = () => { setAllocs(null); setAlloc(null); setQty(""); setPhotos([]); setConfirmed(null); setNote(""); setOver(null); setOverNotes({}); setClientId(newClientId()); };

  const lookup = useCallback(async (c) => {
    const kode = c.trim().toUpperCase();
    if (!kode) return;
    try {
      const { data } = await api.get(`/final-checks/lookup?code=${encodeURIComponent(kode)}`);
      beep("ok");
      setAllocs(data.allocations); setAlloc(data.allocations.length === 1 ? data.allocations[0] : null);
      setQty(""); setPhotos([]); setConfirmed(null); setNote(""); setClientId(newClientId());
      setCode("");
    } catch (e) { beep("error"); setAllocs(null); setAlloc(null); toast.error(fmtErr(e)); }
  }, []);
  const onCameraScan = useCallback((txt) => { setCode(txt.toUpperCase()); lookup(txt); }, [lookup]);

  const simpan = async (notesOverride) => {
    if (!alloc) return;
    const q = Number(qty);
    if (!qty || !(q > 0) || !Number.isInteger(q)) { toast.error("Qty aktual harus angka bulat positif"); return; }
    if (!photos.length) { toast.error("Foto bukti material wajib"); return; }
    if (confirmed === null) { toast.error("Pilih konfirmasi kesesuaian: IYA / TIDAK"); return; }
    if (!confirmed && !note.trim()) { toast.error("Keterangan ketidaksesuaian wajib untuk TIDAK"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/final-checks", { markingId: alloc.markingId, qty: q, confirmed, note, photos, overNote: (notesOverride || overNotes)[alloc.markingId] || "", clientId });
      beep("ok");
      toast.success(data.duplicate ? "Sudah tersimpan (pengiriman ganda diabaikan)" : `Final check ${alloc.code} tersimpan — ${q} pcs ${confirmed ? "IYA" : "TIDAK"}`);
      reset(); setRefreshKey((k) => k + 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 422 && d?.code === "OVER_TARGET") { setOver(d.rows); return; }
      beep("error"); toast.error(fmtErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader id="Material Control & Logistik" zh="物料控制与物流" desc="Final check material: scan barcode → qty aktual → foto bukti → konfirmasi IYA/TIDAK. Riwayat & hasil final check berdiri sendiri — tidak terhubung dengan input progress admin maupun QC." />

      <Tabs defaultValue="final">
        <TabsList className="bg-slate-900/70 border border-slate-800" data-testid="mc-tabs">
          <TabsTrigger value="final" data-testid="mc-tab-final"><ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Final Check <span className="cn ml-1 text-slate-500">终检</span></TabsTrigger>
          <TabsTrigger value="riwayat" data-testid="mc-tab-riwayat"><History className="mr-1.5 h-3.5 w-3.5" />Riwayat Final Check <span className="cn ml-1 text-slate-500">终检记录</span></TabsTrigger>
        </TabsList>

        <TabsContent value="final">
          <div className="mt-3 grid gap-4 lg:grid-cols-[1fr_1.2fr]">
            <div className="panel scanlines relative overflow-hidden p-5" data-testid="mc-scan-panel">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden"><div className="sweep h-full w-1/3 bg-gradient-to-r from-transparent via-amber-400 to-transparent" /></div>
              <div className="flex items-center gap-2 text-xs text-slate-400"><ScanLine className="h-4 w-4 text-amber-400" /> Scan label QR dengan kamera, scanner USB, atau ketik kode marking</div>
              <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(code); }}>
                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="K03-010…" className="border-slate-800 bg-slate-900/70 font-mono" data-testid="mc-scan-input" />
                <Button type="submit" variant="outline" className="border-slate-700" data-testid="mc-lookup-button">Cari</Button>
              </form>
              <div className="mt-3"><CameraScanner onScan={onCameraScan} accent="amber" testid="mc-camera" /></div>
              <p className="mt-3 text-[11px] text-slate-500">Alur: SCAN → DATA MARKING → QTY AKTUAL → FOTO → KONFIRMASI IYA/TIDAK → SIMPAN. Scan ulang tidak menambah qty; setiap pemeriksaan tercatat dengan foto & pemeriksa.</p>
            </div>

            <div className="panel p-5" data-testid="mc-form-panel">
              {!allocs ? (
                <div className="rounded border border-dashed border-slate-800 p-10 text-center text-sm text-slate-600" data-testid="mc-empty-state">Belum ada marking dipindai<br /><span className="cn text-xs">请扫描构件条码</span></div>
              ) : (
                <>
                  {allocs.length > 1 && (
                    <div className="mb-3" data-testid="mc-alloc-picker">
                      <div className="mb-1 text-[11px] uppercase tracking-wider text-amber-300">Marking ini punya {allocs.length} alokasi — pilih SPK/vendor yang sesuai</div>
                      <div className="grid gap-1.5 sm:grid-cols-2">
                        {allocs.map((a) => (
                          <button type="button" key={a.markingId} onClick={() => setAlloc(a)} className={cn("rounded border p-2 text-left text-xs", alloc?.markingId === a.markingId ? "border-amber-500/60 bg-amber-500/10" : "border-slate-800 hover:border-slate-600")} data-testid={`mc-alloc-${a.spkNo}`}>
                            <div className="font-mono text-slate-200">{a.spkNo}</div><div className="text-slate-400">{a.vendorName} · {a.projectKode} · target {a.target} pcs</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {alloc && (
                    <div data-testid="mc-marking-card">
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <div className="font-mono text-2xl font-bold text-amber-400">{alloc.code}</div>
                        <div className="text-sm text-slate-300">{alloc.profile || "—"}</div>
                        <div className="ml-auto text-xs text-slate-400">{alloc.projectKode} · {alloc.projectName}</div>
                      </div>
                      <div className="mt-1 text-xs text-slate-400">SPK <span className="font-mono text-slate-200">{alloc.spkNo}</span> · Vendor <span className="text-slate-200">{alloc.vendorName}</span> · Berat {alloc.unitBeratKg ? `${alloc.unitBeratKg} kg/pcs` : <span className="text-rose-300">belum tersedia</span>}</div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        {[["Target alokasi", alloc.target, "text-slate-100"], ["Aktual terkonfirmasi (IYA)", alloc.confirmedCum, "text-emerald-300"], ["Sisa ke target", Math.max(0, alloc.target - alloc.confirmedCum), "text-amber-300"]].map(([l, v, c]) => (
                          <div key={l} className="rounded bg-slate-900/70 p-2"><div className="text-[9px] leading-tight text-slate-500">{l}</div><div className={cn("font-mono text-lg", c)}>{v}</div></div>
                        ))}
                      </div>
                      <div className="mt-1 text-right"><TargetChip ts={alloc.targetStatus} /></div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <div>
                          <div className="mb-1 text-[11px] text-slate-500">Qty Aktual Pemeriksaan (pcs) *</div>
                          <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="" className="border-slate-800 bg-slate-900/70 font-mono text-lg" data-testid="mc-qty-input" />
                        </div>
                        <div>
                          <div className="mb-1 text-[11px] text-slate-500">Konfirmasi kesesuaian material *</div>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setConfirmed(true)} className={cn("rounded border py-2 text-sm font-semibold", confirmed === true ? "border-emerald-500 bg-emerald-500/20 text-emerald-200" : "border-slate-800 text-slate-400 hover:border-emerald-500/50")} data-testid="mc-confirm-yes">IYA <span className="cn font-normal">是</span></button>
                            <button type="button" onClick={() => setConfirmed(false)} className={cn("rounded border py-2 text-sm font-semibold", confirmed === false ? "border-rose-500 bg-rose-500/20 text-rose-200" : "border-slate-800 text-slate-400 hover:border-rose-500/50")} data-testid="mc-confirm-no">TIDAK <span className="cn font-normal">否</span></button>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3"><div className="mb-1 text-[11px] text-slate-500">Foto Bukti Material *</div><PhotoCapture photos={photos} onChange={setPhotos} testid="mc-photo" /></div>
                      <div className="mt-3">
                        <div className="mb-1 text-[11px] text-slate-500">Keterangan {confirmed === false && <span className="text-rose-300">* wajib (ketidaksesuaian)</span>}</div>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={confirmed === false ? "jelaskan ketidaksesuaian material…" : "opsional"} className="border-slate-800 bg-slate-900/70 text-sm" data-testid="mc-note-input" />
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button type="button" variant="outline" onClick={reset} className="border-slate-700" data-testid="mc-cancel-button">Batal</Button>
                        <Button type="button" onClick={() => simpan()} disabled={busy} className="flex-1 bg-amber-600 hover:bg-amber-500 text-[#0B1220] font-semibold" data-testid="mc-save-button">{busy ? "Menyimpan…" : "Simpan Final Check / 保存终检"}</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="riwayat">
          <div className="mt-3"><FinalCheckHistory refreshKey={refreshKey} /></div>
        </TabsContent>
      </Tabs>

      {over && <OverTargetDialog title="Aktual terkonfirmasi melebihi target alokasi" rows={over} notes={overNotes} onNotes={setOverNotes} onNo={() => setOver(null)} onYes={() => { const n = overNotes; setOver(null); simpan(n); }} />}
    </div>
  );
}
